<template>
  <div
    ref="tableContainer"
    class="overflow-x-clip"
    @mousedown="onMouseDown"
    @mousemove="onMouseMove"
    @mouseup="onMouseUp"
    @mouseleave="onMouseUp"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <table
      ref="tableElement"
      class="w-full relative"
      :style="tablePositionLeft"
    >
      <thead>
        <tr>
          <th
            v-for="productData in productsData"
            :key="productData.product.sku"
            class="px-2"
            :style="tablePositionTop"
          >
            <CustomLink
              :url="productData.product._slug"
              :type="LINK_TYPE.NoStyle"
              @link-click="onClick(productData.product)"
            >
              <TwicpicsImage
                v-if="productData.product.images?.length"
                :src="productData.product.images[0].url"
                :alt="productData.product._name"
                :width="240"
                :height="240"
                class="max-h-60"
              />
            </CustomLink>
          </th>
        </tr>
        <tr
          class="relative"
        >
          <th
            v-for="productData in productsData"
            :key="productData.product.sku"
            class="sticky bg-EXPONDO-white font-normal text-left
              p-2 lg:pt-4 min-w-[176px] w-72 align-top z-[2]"
            :style="[tablePositionTop]"
          >
            <CustomLink
              :url="productData.product._slug"
              :type="LINK_TYPE.NoStyle"
              @link-click="onClick(productData.product)"
            >
              <AlternativeProductDetails
                :product="productData.product"
                :current-sku="currentSku"
              />
            </CustomLink>
            <div
              class="absolute shadow-bottom bottom-0 w-full h-1"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <template
          v-for="({key, values}, index) in attributes"
          :key="`${key}-label`"
        >
          <ComparisonTableRow
            :row-number="index + 1"
            :show-more="showMore"
            class="bg-EXPONDO-grey-5"
          >
            <th
              colspan="100%"
              class="text-left font-bold px-2"
            >
              <div
                class="relative"
                :style="tablePositionLeftReverse"
              >
                {{ key }}
              </div>
            </th>
          </ComparisonTableRow>
          <ComparisonTableRow
            :row-number="index + 1"
            :show-more="showMore"
          >
            <td
              v-for="productData in productsData"
              :key="`${productData.product.sku}`"
              class="px-2"
            >
              <div
                v-for="(value, i) in values[productData.product.sku]"
                :key="i"
              >
                {{ value }}
              </div>
            </td>
          </ComparisonTableRow>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">

import { useResizeObserver } from '@vueuse/core';

import type { AttributesArray } from '~~/types/product/ComparisonProducts';
import type { AlternativeProductData } from '~~/types/product/AlternativeProducts';
import { LINK_TYPE } from '~~/types/components/Link/LinkType';
import { CLICK_PRODUCT_IN_COMPARISON_TABLE } from '~~/constants/googleTagManager';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import { getProductIsOnStock } from '~~/helpers/product/getProductStockInfo/getProductStockInfo';
import { DEFAULT_ATTRIBUTE_LIMIT } from '~~/constants/alternativeProducts';

export default defineComponent({
  name: 'ComparisonTable',

  props: {
    productsData: {
      type: Array as PropType<AlternativeProductData[]>,
      required: true,
    },
    attributes: {
      type: Array as PropType<AttributesArray>,
      required: true,
    },
    currentSku: {
      type: String,
      required: true,
    },
    showMore: {
      type: Boolean,
      required: true,
    },
  },
  setup() {

    const { isMobile } = useUiMode();
    const { stickyAnchorNavHeight } = useAnchorNavigationStore();
    const { trackViewProduct } = useGtmEvents();

    const tableContainer = ref(null);
    const tableElement = ref(null);
    const isDragging = ref(false);
    const tablePosition = ref(0);
    const startPosition = ref(0);
    const scrollSpace = ref(0);

    const setScrollSpace = (): void => {

      if (!tableElement.value || !tableContainer.value) {

        return;
      
      }
      scrollSpace.value = tableElement.value.offsetWidth - tableContainer.value.offsetWidth;
    
    };
    const setStartPosition = (eventPosition: number): void => {

      startPosition.value = tablePosition.value + eventPosition;
    
    };
    const setCurrentPosition = (eventPosition: number): void => {

      const newPosition = startPosition.value - eventPosition;

      if (newPosition < 0) {

        tablePosition.value = 0;

        return;
      
      }

      if (newPosition > scrollSpace.value) {

        tablePosition.value = scrollSpace.value;

        return;
      
      }
      tablePosition.value = newPosition;
    
    };

    const tablePositionTop = computed(() => {

      return { top: `${isMobile.value ? 0 : stickyAnchorNavHeight.value}px` };
    
    });
    const tablePositionLeft = computed(() => {

      return { left: `${-tablePosition.value}px` };
    
    });
    const tablePositionLeftReverse = computed(() => {

      return { left: `${tablePosition.value}px` };
    
    });

    const resizeCallback = (): void => {

      setScrollSpace();
      setStartPosition(0);
      setCurrentPosition(0);
    
    };

    useResizeObserver(tableContainer, resizeCallback);

    const onMouseDown = (event: MouseEvent): void => {

      isDragging.value = true;
      setStartPosition(event.clientX);
    
    };
    const onMouseMove = (event: MouseEvent): void => {

      if (isDragging.value) {

        setCurrentPosition(event.clientX);
      
      }
    
    };
    const onMouseUp = (): void => {

      isDragging.value = false;
    
    };

    const onTouchStart = (event: TouchEvent): void => {

      isDragging.value = true;
      setStartPosition(event.touches[0].clientX);
    
    };
    const onTouchMove = (event: TouchEvent): void => {

      if (isDragging.value) {

        setCurrentPosition(event.touches[0].clientX);
      
      }
    
    };
    const onTouchEnd = (): void => {

      isDragging.value = false;
    
    };
    const onClick = (product: ProductVariantExtended) => {

      trackViewProduct({
        product,
        inStock: getProductIsOnStock(product),
        eventName: CLICK_PRODUCT_IN_COMPARISON_TABLE,
      });
    
    };

    return {
      tableContainer,
      tableElement,
      isDragging,
      tablePositionTop,
      tablePositionLeft,
      tablePositionLeftReverse,
      startPosition,
      tablePosition,
      scrollSpace,
      onMouseDown,
      onMouseUp,
      onMouseMove,
      onTouchStart,
      onTouchEnd,
      onTouchMove,
      LINK_TYPE,
      onClick,
      getProductIsOnStock,
      DEFAULT_ATTRIBUTE_LIMIT,
    };
  
  },
});


</script>
